// components.js
import { colors } from "./theme";

const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

/* ---------- Cards / Surfaces ---------- */

export const surfaces = {
  /**
   * Base card
   * Mobile-friendly padding & radius
   */
  card: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.border,
    transition: "box-shadow 0.15s ease, border-color 0.15s ease",
    textAlign: "center",
    boxSizing: "border-box",
  },

  /**
   * Hoverable card (desktop-focused)
   */
  cardHoverable: {
    cursor: "pointer",
    outline: "none",
    boxShadow: "none",
  },

  /**
   * Hover state (applied conditionally in components)
   */
  cardHoverableHover: {
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    borderColor: colors.primarySoft,
  },
};

/* ---------- Buttons ---------- */

/**
 * Button base
 * 44px+ tap target guaranteed
 */
const buttonBase = {
  minHeight: 44,
  padding: "14px 22px",
  fontSize: "1rem",
  fontWeight: 600,
  borderRadius: 8,
  cursor: "pointer",
  transition: "all 0.15s ease",
  width: "auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
};

/**
 * Button variants
 */
export const buttons = {
  primary: {
    ...buttonBase,
    background: colors.primary,
    color: colors.white,
    border: "none",
    boxShadow: isMobile
      ? "none"
      : "0 8px 20px rgba(31,107,79,0.25)",
  },

  secondary: {
    ...buttonBase,
    background: colors.white,
    color: colors.textDark,
    border: "1px solid #cfc6b6",
  },

  danger: {
    ...buttonBase,
    background: colors.danger,
    color: colors.white,
    border: "none",
  },
};

/* ---------- Forms ---------- */

export const form = {
  /**
   * Labels
   */
  label: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: colors.text,
    marginBottom: 6,
    display: "block",
  },

  /**
   * Inputs
   * Mobile-friendly height & font
   */
  input: {
    width: "100%",
    minHeight: 44,
    padding: "12px 14px",
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    fontSize: "1rem",
    lineHeight: 1.4,
    boxSizing: "border-box",
  },

  /**
   * Error text
   */
  error: {
    color: colors.danger,
    fontSize: "0.9rem",
    marginTop: 4,
  },
};

/* ---------- Tour Button ---------- */

export const tourButton = {
  base: {
    padding: "8px 14px",
    borderRadius: "50px",
    border: "1px solid #ccc",
    backgroundColor: "#f9f9f9",
    color: "#333",
    cursor: "pointer",
    fontWeight: 500,
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  hover: {
    backgroundColor: "#f0f0f0",
    boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
    transform: "translateY(-1px)",
  },
};

/* ---------- Rate Box ---------- */

export const rateBox = {
  marginTop: "24px",
  background: "#fdfdfd",
  padding: "16px 20px",
  borderRadius: "8px",
  border: "1px solid #ddd",
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  maxWidth: "500px",
};

/* ---------- Toast Notifications ---------- */

export const toastOptions = {
  position: "top-center",
  duration: 4000,
  style: {
    borderRadius: "8px",
    background: "#333",
    color: "#fff",
    padding: "10px 14px",
  },
  success: {
    iconTheme: {
      primary: "#007BFF",
      secondary: "#fff",
    },
  },
  error: {
    iconTheme: {
      primary: "#E74C3C",
      secondary: "#fff",
    },
  },
};

/* ---------- Divider ---------- */

export const divider = {
  borderTop: "1px solid #eee",
  margin: "16px 0",
};

/* ---------- Container ---------- */

export const containerStyle = {
  padding: "24px",
  fontFamily: "Inter, sans-serif",
  color: "#222",
};

/* ---------- Header Buttons ---------- */

const headerBtn = {
  padding: "8px 14px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  backgroundColor: "#f0f0f0",
  color: "#333",
  cursor: "pointer",
  fontWeight: 500,
};

export const headerBtnPrimary = {
  ...headerBtn,
  backgroundColor: "#007BFF",
  color: "#fff",
  border: "none",
};

export const headerBtnDanger = {
  ...headerBtn,
  backgroundColor: "red",
  color: "#fff",
  border: "none",
};

/* ---------- Modals ---------- */

export const modal = {
  overlay: {
    position: "fixed",
    inset: 0,
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? 16 : 20,
    zIndex: 1000,
  },
  
  box: {
    background: "#fff",
    borderRadius: isMobile ? 8 : 10,
    padding: isMobile ? 16 : 24,
    maxWidth: isMobile ? "95%" : "500px",
    width: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
  },
  
  boxLarge: {
    background: "#fff",
    borderRadius: isMobile ? 8 : 10,
    padding: isMobile ? 16 : 24,
    maxWidth: isMobile ? "95vw" : "780px",
    width: isMobile ? "95%" : "100%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
  },
};
